import React from "react";
import { SidebarTW } from "./SidebarTW";
import { useAuth } from "../../contexts/AuthContext";
import { useSidebarContext } from "../../contexts/SidebarContext";
import { FooterTW } from "./FooterTW";

export interface SidebarLayoutProps {
  children: React.ReactNode;
  className?: string;
  /** Show footer (default: false for sidebar layout) - nur im Standalone-Modus aktiv */
  showFooter?: boolean;
}

/**
 * SidebarLayout - Layout mit vertikaler Sidebar-Navigation (Tailwind-basiert)
 *
 * Wird für eingeloggte Benutzer verwendet.
 *
 * WICHTIG: Wenn die Sidebar bereits auf App-Level existiert (AuthenticatedLayout),
 * wird nur der Content-Wrapper gerendert, NICHT die Sidebar selbst.
 * Dies verhindert doppeltes Rendern und sorgt für nahtlose Navigation.
 *
 * Struktur:
 * ┌─────────────┬──────────────────────────────────────┐
 * │             │                                      │
 * │   Sidebar   │          Main Content                │
 * │   (fixed)   │          (scrollable)                │
 * │             │                                      │
 * │             │                                      │
 * └─────────────┴──────────────────────────────────────┘
 */
export const SidebarLayout: React.FC<SidebarLayoutProps> = ({
  children,
  className = "",
  showFooter = false,
}) => {
  const { isAuthenticated } = useAuth();
  const { hasSidebar } = useSidebarContext();

  // WICHTIG: Context-Check ZUERST!
  // Wenn hasSidebar true ist, wissen wir:
  // 1. Der User ist durch ProtectedRoute gekommen (also authentifiziert)
  // 2. AuthenticatedLayout rendert bereits die Sidebar, den Hintergrund UND den Footer
  // -> Nur den Content als flex-Wrapper rendern
  if (hasSidebar) {
    return (
      <div className={`flex flex-col flex-1 ${className}`}>
        {children}
      </div>
    );
  }

  // Fallback: Wenn nicht eingeloggt, zeige einfaches Layout ohne Sidebar
  if (!isAuthenticated) {
    return (
      <div className={`min-h-screen flex flex-col bg-gray-100 dark:bg-gray-950 ${className}`}>
        <main className="transition-all duration-300 flex-grow">
          {children}
        </main>
        {showFooter && <FooterTW />}
      </div>
    );
  }

  // Fallback: Standalone-Modus für authentifizierte User ohne App-Level-Sidebar
  // (z.B. wenn eine Seite direkt aufgerufen wird ohne durch App.tsx zu gehen)
  return (
    <div className={`min-h-screen bg-gray-100 dark:bg-gray-950 ${className}`}>
      {/* Sidebar (fixed left) - Tailwind-only mit integriertem Mobile-Trigger */}
      <SidebarTW />

      {/* Main Content Area - SidebarTW ist fix 220px breit ab xl */}
      <main className="min-h-screen xl:pl-[220px]">
        <div className="flex flex-col min-h-screen">
          {/* Page Content */}
          <div className="flex-grow relative z-10">
            {children}
          </div>

          {/* Optional Footer - liegt ueber fixed-Hintergrundelementen der Seite */}
          {showFooter && (
            <div className="relative z-10">
              <FooterTW />
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

/**
 * SidebarPage - Wrapper für Seiten-Inhalte innerhalb des SidebarLayouts
 *
 * Stellt konsistente Abstände und maximale Breite sicher.
 */
export interface SidebarPageProps {
  children: React.ReactNode;
  className?: string;
  /** Title for the page (optional) */
  title?: string;
  /** Description/subtitle (optional) */
  description?: string;
  /** Actions to show in the header (optional) */
  actions?: React.ReactNode;
  /** Full width content (no max-width constraint) */
  fullWidth?: boolean;
}

export const SidebarPage: React.FC<SidebarPageProps> = ({
  children,
  className = "",
  title,
  description,
  actions,
  fullWidth = false,
}) => {
  return (
    <div
      className={`
        w-full py-6 px-4 sm:px-6 lg:px-8
        ${fullWidth ? "" : "max-w-7xl mx-auto"}
        ${className}
      `}
    >
      {/* Page Header (optional) */}
      {(title || actions) && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            {title && (
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{title}</h1>
            )}
            {description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>
            )}
          </div>
          {actions && <div className="flex items-center gap-3">{actions}</div>}
        </div>
      )}

      {/* Page Body */}
      <div>{children}</div>
    </div>
  );
};
